import type {
  UploadService,
  ValidationResult,
  ImageFile,
  UploadProgress,
} from '../contracts/uploadService'
import { ExifExtractorService } from './ExifExtractorService'

export class DefaultUploadService implements UploadService {
  private batches = new Map<string, ImageFile[]>()
  private progress = new Map<string, UploadProgress>()
  private exifExtractor = new ExifExtractorService()
  private supportedTypes = [
    // Image formats
    'image/jpeg', 'image/png', 'image/webp', 'image/gif',
    // Video formats
    'video/mp4', 'video/quicktime', 'video/avi', 'video/webm', 'video/mov'
  ]
  private maxFileSize = 500 * 1024 * 1024 // 500MB (increased for video files)
  private maxBatchSize = 300

  validateFiles(files: FileList): ValidationResult[] {
    const results: ValidationResult[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)
      if (!file) continue

      const validation = this.validateSingleFile(file)
      results.push(validation)
    }

    return results
  }

  async processFiles(files: FileList, onProgress?: (progress: UploadProgress) => void): Promise<ImageFile[]> {
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    const imageFiles: ImageFile[] = []

    // Convert FileList to Array to avoid any potential issues with FileList volatility
    const fileArray = Array.from(files)
    const totalFiles = fileArray.length

    // Initialize progress
    const initialProgress = {
      batchId,
      totalFiles,
      processedFiles: 0,
      status: 'initializing' as const,
    }
    this.progress.set(batchId, initialProgress)
    onProgress?.(initialProgress)

    const failedFiles: Array<{file: File, error: Error}> = []

    try {
      console.log(`🔄 Starting batch processing of ${totalFiles} files`)

      for (let i = 0; i < fileArray.length; i++) {
        console.log(`🔄 Loop iteration ${i + 1}/${totalFiles}`)

        const file = fileArray[i]
        if (!file) {
          console.warn(`❌ File at index ${i} is null, skipping`)
          continue
        }

        console.log(`📋 Validating file: ${file.name}`)

        // Validate file before processing
        const validation = this.validateSingleFile(file)
        if (!validation.isValid) {
          console.warn(`❌ Skipping invalid file ${file.name}:`, validation.errors)
          failedFiles.push({file, error: new Error(validation.errors[0]?.message || 'File validation failed')})
          continue
        }

        console.log(`✅ File ${file.name} passed validation`)

        // Update progress - starting file processing
        const currentProgress = {
          batchId,
          totalFiles,
          processedFiles: i,
          currentFile: file.name,
          currentFileStatus: 'uploading' as const,
          status: 'processing' as const,
        }
        this.progress.set(batchId, currentProgress)
        onProgress?.(currentProgress)

        console.log(`🔄 Processing file ${i + 1}/${totalFiles}: ${file.name}`)

        try {
          const imageFile = await this.processFile(file, i, batchId, onProgress)
          imageFiles.push(imageFile)
          console.log(`✅ Successfully processed ${file.name} (${imageFiles.length}/${totalFiles} completed)`)
        } catch (fileError) {
          console.error(`❌ Failed to process ${file.name}:`, fileError)
          failedFiles.push({file, error: fileError instanceof Error ? fileError : new Error('Unknown processing error')})
          // Continue processing other files instead of stopping the entire batch
        }

        console.log(`🔄 Completed processing attempt for file ${i + 1}/${totalFiles}`)
      }

      console.log(`🏁 Finished processing all files. Processed: ${imageFiles.length}, Failed: ${failedFiles.length}`)

      // Complete progress with information about failed files
      const completedProgress = {
        batchId,
        totalFiles,
        processedFiles: imageFiles.length,
        status: (failedFiles.length > 0 ? 'completed-with-errors' : 'completed') as const,
        failedFiles: failedFiles.length > 0 ? failedFiles.map(f => f.file.name) : undefined,
      }
      this.progress.set(batchId, completedProgress)
      onProgress?.(completedProgress)

      // Log summary
      console.log(`📊 Batch processing summary:`)
      console.log(`  ✅ Successful: ${imageFiles.length} files`)
      console.log(`  ❌ Failed: ${failedFiles.length} files`)
      if (failedFiles.length > 0) {
        console.log(`  Failed files:`, failedFiles.map(f => `${f.file.name}: ${f.error.message}`))
      }

      this.batches.set(batchId, imageFiles)
      return imageFiles

    } catch (error) {
      // Error progress - this should rarely happen now since we handle individual file errors
      const errorProgress = {
        batchId,
        totalFiles,
        processedFiles: imageFiles.length,
        status: 'error' as const,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
      this.progress.set(batchId, errorProgress)
      onProgress?.(errorProgress)
      console.error('❌ Batch processing failed with unexpected error:', error)
      throw error
    }
  }

  getUploadProgress(batchId: string): UploadProgress {
    return this.progress.get(batchId) || {
      batchId,
      totalFiles: 0,
      processedFiles: 0,
      status: 'initializing',
    }
  }

  async cancelUpload(batchId: string): Promise<void> {
    this.progress.delete(batchId)
    this.batches.delete(batchId)
  }

  getSupportedTypes(): string[] {
    return [...this.supportedTypes]
  }

  getMaxFileSize(): number {
    return this.maxFileSize
  }

  getMaxBatchSize(): number {
    return this.maxBatchSize
  }

  private validateSingleFile(file: File): ValidationResult {
    const errors: ValidationResult['errors'] = []
    const warnings: ValidationResult['warnings'] = []

    // Check file type
    if (!this.supportedTypes.includes(file.type)) {
      errors.push({
        code: 'INVALID_FILE_TYPE',
        message: `File type ${file.type} is not supported. Supported types: ${this.supportedTypes.join(', ')}`,
        severity: 'error',
      })
    }

    // Check file size
    if (file.size > this.maxFileSize) {
      errors.push({
        code: 'FILE_TOO_LARGE',
        message: `File size ${Math.round(file.size / 1024 / 1024)}MB exceeds maximum ${Math.round(this.maxFileSize / 1024 / 1024)}MB`,
        severity: 'error',
      })
    }

    // Check for potential issues (different thresholds for images vs videos)
    const isVideo = file.type.startsWith('video/')
    const minSize = isVideo ? 1 * 1024 * 1024 : 100 * 1024 // 1MB for videos, 100KB for images
    
    if (file.size < minSize) {
      warnings.push({
        code: 'SMALL_FILE_SIZE',
        message: isVideo 
          ? 'Video file is quite small, which may affect quality for stock footage'
          : 'File is quite small, which may affect image quality for stock photography',
        severity: 'warning',
      })
    }

    // Check file name
    if (file.name.length > 100) {
      warnings.push({
        code: 'LONG_FILENAME',
        message: 'Filename is quite long, consider shortening for better compatibility',
        severity: 'warning',
      })
    }

    return {
      file,
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  }

  private async processFile(file: File, index: number, batchId: string, onProgress?: (progress: UploadProgress) => void): Promise<ImageFile> {
    const id = `img-${Date.now()}-${index}-${Math.random().toString(36).substring(2, 9)}`

    // Check if file needs compression (>5MB)
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > 5) {
      // Update progress to show compression
      const compressionProgress = {
        ...this.progress.get(batchId)!,
        currentFileStatus: 'compressing' as const,
      }
      this.progress.set(batchId, compressionProgress)
      onProgress?.(compressionProgress)
    }

    // Update progress to show preview generation
    const previewProgress = {
      ...this.progress.get(batchId)!,
      currentFileStatus: 'generating-preview' as const,
    }
    this.progress.set(batchId, previewProgress)
    onProgress?.(previewProgress)

    const isVideo = file.type.startsWith('video/')
    const preview = isVideo ? await this.generateVideoPreview(file) : await this.generateImagePreview(file)
    const dimensions = isVideo ? await this.extractVideoDimensions(file) : await this.extractImageDimensions(file)

    // Extract EXIF data for image files
    let exifData
    if (!isVideo && this.exifExtractor.getSupportedTypes().includes(file.type)) {
      try {
        exifData = await this.exifExtractor.extractExifData(file, {
          validateData: true,
          convertCoordinates: true
        })
      } catch (error) {
        // EXIF extraction is optional - don't fail the upload if it fails
        console.warn(`Failed to extract EXIF data from ${file.name}:`, error)
      }
    }

    // Update progress to show file completed
    const completedProgress = {
      ...this.progress.get(batchId)!,
      currentFileStatus: 'completed' as const,
    }
    this.progress.set(batchId, completedProgress)
    onProgress?.(completedProgress)

    return {
      id,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date(),
      processingStatus: 'completed',
      preview,
      dimensions,
      exifData,
    }
  }

  private async generateImagePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          // Calculate dimensions for preview (max 200x200)
          const maxSize = 200
          let { width, height } = img

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height
              height = maxSize
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)

          resolve(canvas.toDataURL('image/jpeg', 0.8))
        }
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target?.result as string
      }

      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  private async extractImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
          })
        }
        img.onerror = () => reject(new Error('Failed to load image for dimension extraction'))
        img.src = e.target?.result as string
      }

      reader.onerror = () => reject(new Error('Failed to read file for dimension extraction'))
      reader.readAsDataURL(file)
    })
  }

  private async generateVideoPreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      video.preload = 'metadata'
      video.muted = true
      
      video.onloadedmetadata = () => {
        // Seek to 1 second (or 10% of video duration, whichever is smaller)
        const seekTime = Math.min(1, video.duration * 0.1)
        video.currentTime = seekTime
      }

      video.onseeked = () => {
        try {
          // Calculate dimensions for preview (max 200x200)
          const maxSize = 200
          let { videoWidth: width, videoHeight: height } = video

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height
              height = maxSize
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(video, 0, 0, width, height)

          resolve(canvas.toDataURL('image/jpeg', 0.8))
        } catch (error) {
          reject(new Error('Failed to generate video preview'))
        }
      }

      video.onerror = () => reject(new Error('Failed to load video for preview'))
      
      // Create object URL for the video file
      const url = URL.createObjectURL(file)
      video.src = url
      
      // Clean up object URL after processing
      video.addEventListener('loadedmetadata', () => {
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      })
    })
  }

  private async extractVideoDimensions(file: File): Promise<{ width: number; height: number; duration?: number }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      
      video.preload = 'metadata'
      video.muted = true

      video.onloadedmetadata = () => {
        try {
          const dimensions = {
            width: video.videoWidth,
            height: video.videoHeight,
            duration: video.duration
          }
          resolve(dimensions)
        } catch (error) {
          reject(new Error('Failed to extract video dimensions'))
        }
      }

      video.onerror = () => reject(new Error('Failed to load video for dimension extraction'))
      
      // Create object URL for the video file
      const url = URL.createObjectURL(file)
      video.src = url
      
      // Clean up object URL after processing
      video.addEventListener('loadedmetadata', () => {
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      })
    })
  }
}
